<template>
  <nav
    :class="{ 'mainnav--sticky': sticky }"
    class="mainnav"
  >
    <div class="mainnav__wrapper">
      <div class="mainnav__logo-wrapper">
        <nuxt-link
          :to="{ name: 'index' }"
          class="mainnav__logo"
          data-tealium-rel="navi-top.image.logo"
          title="Zur Startseite"
        >
          <SvgIcon
            :image="logo"
            no-icon-class
            aria-label="Telekom Logo"
            :aria-hidden="false"
          />
        </nuxt-link>
      </div>

      <div
        v-if="!hideNavi"
        class="mainnav__desktop-menus"
      >
        <CustomerNavigation ref="customerNavigation" />
        <div class="mainnav__inner-menus">
          <PrimaryNavigation @subnav-open="toggleSubnav" />

          <SecondaryNavigation
            @search="search"
            @toggle-mobile-nav="toggleMobileNav"
          />
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from '#imports';
import focusableSelectors from 'focusable-selectors';
import { urlifyTermAndSection } from '@/lib/search-helper';
import PrimaryNavigation from '@/components/navigation/primary-navigation.vue';
import SecondaryNavigation from '@/components/navigation/secondary-navigation.vue';
import logo from '@/assets/images/telekom-logo-magenta-bg.svg';
import CustomerNavigation from '@/components/customer-navigation.vue';
import useScrollAnimation from '@/components/anker/animations/scroll-animation';

export interface MainNavigationProps {
  sticky?: boolean;
  hideNavi?: boolean;
}
const props = withDefaults(defineProps<MainNavigationProps>(), {});

const { createScrollTrigger, createMatchMedia, createTweenTo } = useScrollAnimation();

const $route = useRoute();
const $router = useRouter();

const isMenuOpen = ref<boolean>(false);
const isSubnavOpen = ref<boolean>(false);

const search = (term) => {
  if (!term) return;
  const route = urlifyTermAndSection(term, 'alle');
  $router.push(route);
};
const toggleMobileNav = (isOpen) => {
  // on a ssr redirect, this will be called and throw an error.
  if (process.server) return;
  const body = document.querySelector('body');
  if (!body) return;
  if (isOpen) {
    body.classList.add('mobile-nav-open');
    isMenuOpen.value = true;
  } else {
    body.classList.remove('mobile-nav-open');
    isMenuOpen.value = false;
  }
};

const filteredFocusableElements = computed(() => {
  const navigationElement = document.querySelector('.mainnav__inner-menus');

  return !isSubnavOpen.value
    ? Array.from(
        navigationElement.querySelectorAll(
          focusableSelectors
            .map(
              (sel) =>
                `${sel}:not(.main-navigation-item__subnav *):not(.main-navigation-item__icon-button--desktop *)`
            )
            .join(',')
        )
      )
    : Array.from(
        navigationElement.querySelectorAll(
          focusableSelectors
            .map(
              (sel) =>
                `${sel}:not(.main-navigation-item__icon-button):not(.main-navigation-item__icon)`
            )
            .join(',')
        )
      ).filter((el) => {
        if (el.classList.contains('main-navigation-item__link')) {
          return (
            el.closest('.main-navigation-item--expanded') !== null &&
            el.closest('.main-navigation-item__subnav') !== null
          );
        }
        if (el.classList.contains('.main-navigation-item__link--big')) {
          return el.classList.contains('.main-navigation-item__subnav-back');
        }
        return true;
      });
});

const handleKeyEvent = (event) => {
  const firstElement = filteredFocusableElements.value[0];

  if (event.key === 'Tab') {
    const currentIndex = filteredFocusableElements.value.indexOf(document.activeElement as any);
    if (currentIndex === -1) {
      event.preventDefault();
      firstElement.focus();
      return;
    }

    if (event.shiftKey) {
      const previousIndex =
        (currentIndex - 1 + filteredFocusableElements.value.length) %
        filteredFocusableElements.value.length;
      event.preventDefault();
      filteredFocusableElements.value[previousIndex].focus();
    } else {
      const nextIndex = (currentIndex + 1) % filteredFocusableElements.value.length;
      event.preventDefault();
      filteredFocusableElements.value[nextIndex].focus();
    }
  }
  if (event.key === 'Escape') {
    const toggleButton = document.querySelector('[aria-controls="primary-navigation"]');
    if (toggleButton) {
      toggleButton.click();
    }
  }
};

const addEventListener = () => {
  document.addEventListener('keydown', handleKeyEvent);
};
const removeEventListener = () => {
  document.removeEventListener('keydown', handleKeyEvent);
};

const toggleSubnav = (isOpen) => {
  isSubnavOpen.value = isOpen;
};
const initAnimations = () => {
  createMatchMedia({
    '(min-width: 1280px)': () => {
      createScrollTrigger({
        trigger: 'body',
        start: 'top+=70px top',
        toggleClass: 'nav-scrolled',
        onLeaveBack: () => {
          document.querySelector('body').classList.remove('nav-scrolled--complete');
        },
        onEnter: () => {
          setTimeout(() => {
            document.querySelector('body').classList.add('nav-scrolled--complete');
          }, 400);
        },
      });
    },
  });
};

watch($route, () => {
  toggleMobileNav(false);
});

watch(isMenuOpen, (val, oldValue) => {
  if (val === oldValue) return;
  if (!val) {
    removeEventListener();
  } else {
    addEventListener();
  }
});

onMounted(() => {
  initAnimations();
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.mainnav {
  background-color: var(--color-bg);
  color: var(--color-font);
  will-change: scroll-position;
  z-index: $z-index-navbar;
  user-select: none;
  height: 60px;
  box-shadow: 0 2px 9px 0 rgb(from var(--color-bg-inverse) r g b / 0.15);
  @include mq($mq-sm) {
    height: 64px;
  }

  @include mq($mq-lg) {
    height: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .primary-navigation {
    @include mq(false, $mq-lg - 1) {
      box-shadow: 0 -1px 2px 0 rgb(from var(--color-bg-inverse) r g b / 0.15);
    }

    @include mq($mq-lg) {
      display: inline-flex;
      width: 100%;
    }
  }

  &__wrapper {
    display: flex;
    align-items: center;
    height: 100%;
    padding-right: $grid-base * 0.5;
    justify-content: space-between;

    @include mq($mq-sm) {
      padding-inline: $grid-base;
    }

    @include mq($mq-lg) {
      width: 100%;
      margin: 0 auto;
      max-width: $max-navbar-width + $grid-base * 4;
      padding-inline: 2 * $grid-base;
      justify-content: flex-start;
    }
  }

  &__logo-wrapper {
    flex: 1 1 100%;
    display: flex;
    align-self: stretch;

    @include mq($mq-lg) {
      align-self: flex-end;
      flex: initial;
      min-width: 96px;
      margin-right: $grid-base * 1.5;
    }
    @include mq($mq-lg) {
      min-width: 138px;
    }
  }

  &__logo {
    display: block;
    height: 100%;
    transition: height 0.4s $nav-timing-function;

    @include mq($mq-lg) {
      height: 96px;
    }

    > svg {
      height: 100%;
      width: auto;
    }
  }

  &__desktop-menus,
  &__inner-menus {
    height: 100%;
    @include mq($mq-lg) {
      width: 100%;
    }
  }

  &__desktop-menus {
    @include mq($mq-lg) {
      max-width: calc(100% - 156px);
    }
  }

  &__inner-menus {
    height: 100%;
    @include mq($mq-lg) {
      display: flex;
      justify-content: space-between;
      grid-gap: $grid-base * 1.5;
    }
  }
}
</style>

<style lang="scss">
@use 'assets/base' as *;
body {
  &.nav-scrolled {
    .mainnav__logo {
      @include mq($mq-lg) {
        height: 70px;
      }
    }

    .nexus-pk-coin {
      @include mq($mq-lg) {
        top: $nav-shrink-offset;
      }
    }
  }

  &.mobile-nav-open {
    @include mq(false, $mq-lg - 1) {
      overflow: hidden;
    }
  }

  &:not(.mobile-nav-open) {
    .primary-navigation {
      display: none;
    }
  }
}

.nexus-pk-coin {
  @include mq($mq-lg) {
    max-width: $max-navbar-width;
    margin: 0 auto;
    transition: top 0.4s $nav-timing-function;
  }
  &__search-wrapper {
    margin-left: 64px + $grid-base;
    height: 100%;
    padding: 0 $grid-base;

    @include mq($mq-lg) {
      margin-left: $grid-base * 10;

      form {
        display: flex;
        align-items: center;
        height: 100%;
        margin-top: 0;
      }
    }
  }
}

// Search Result
.vue-portal-target {
  .nexus-pk-coin {
    // Search Field
    @include mq($mq-sm) {
      padding-top: 4px;
    }
    &__search-wrapper {
      background: var(--color-secondary);
      padding-inline: $grid-base;
    }

    // always show close button
    &__form button.nexus-pk-coin__clear-button {
      display: block;
    }

    &__form {
      margin-block: 11px;
      @include mq($mq-sm) {
        margin-block: $grid-base;
      }
    }
    &__content {
      position: fixed;
      top: 60px;
      left: 0;
      right: 0;
      bottom: 20px;
      max-width: $mq-xl;
      margin-inline: auto;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;

      @include mq($mq-sm) {
        top: 64px;
      }
      @include mq($mq-lg) {
        top: 142px;
        bottom: auto;
        position: absolute;
      }

      > * {
        max-width: 100vw;
      }
    }
  }
}
</style>
